import React from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronDown, X, Package, Calculator, Scale, Wifi } from 'lucide-react';
import { motion } from 'framer-motion';
import { fadeIn } from '../animations';
import MobileBottomNav from '../MobileBottomNav';

/**
 * MobileNavComponent - Renders mobile navigation UI
 * 
 * This component handles the mobile navigation with top bar, menu overlay, and bottom navigation.
 */
const MobileNavComponent = ({
  navItems,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  isCheckingCoverage,
  openCoverageSidebar,
  handleMobileMenuAction,
  isHidden = false,
  openComparisonModal = null,
  openRecommendationModal = null
}) => {
  return (
    <motion.div
      className="md:hidden"
      initial={{ y: 0, opacity: 1 }}
      animate={{
        y: isHidden ? -100 : 0,
        opacity: 1 // Always keep opacity at 1 for a pure slide effect
      }}
      transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1], // Custom cubic-bezier for professional easing
        opacity: { duration: 0.6 }
      }}
    >
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 shadow-md" style={{ backgroundColor: '#121212' }}>
        <div className="flex items-center justify-between px-4 h-14">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src="https://apextech.group/wondernet-logo-white.png"
              alt="Wondernet Logo"
              className="h-7 w-auto"
            />
          </Link>
          
        </div>
      </div>
      
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 pt-14 pb-16 overflow-y-auto"
          style={{ animation: `${fadeIn} 0.2s ease-out`, backgroundColor: 'rgba(18, 18, 18, 0.95)' }}
        >
          <div className="px-4 py-6 space-y-6">
            {/* Navigation Links */}
            <div className="space-y-2">
              {/* Packages Link */}
              <Link
                to="/packages"
                className="flex items-center px-4 py-3 text-white font-medium rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Package className="h-5 w-5 mr-3 text-purple-400" />
                <span>Packages</span>
              </Link>
              
              {/* Regular Nav Items */}
              {navItems.map((item, index) => (
                <div key={index}>
                  {item.path ? (
                    <Link
                      to={item.path}
                      className="block px-4 py-3 text-white font-medium rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ) : (
                    <button
                      className={`w-full text-left px-4 py-3 text-white font-medium rounded-lg ${
                        item.name === 'New'
                          ? 'bg-purple-800/50 border border-purple-500/30' // Highlight the new menu item
                          : 'bg-gray-800/50'
                      } hover:bg-gray-800 transition-colors flex justify-between items-center`}
                      onClick={() => {
                        // For simplicity, just toggle the menu closed
                        setIsMobileMenuOpen(false);
                        // In a real implementation, you might want to show a submenu here
                      }}
                    >
                      {item.name}
                      {item.name === 'New' && <span className="text-xs text-purple-400 mr-2">New!</span>}
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              
              {/* Quick Links */}
              <div className="mt-4 pt-4 border-t border-gray-800">
                <h3 className="text-sm font-medium text-gray-400 px-4 mb-2">Quick Links</h3>
                <button
                  className="flex items-center w-full px-4 py-3 text-white font-medium rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors text-left"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    // Open the recommendation modal if available, otherwise navigate to the recommend page
                    if (openRecommendationModal && typeof openRecommendationModal === 'function') {
                      setTimeout(() => {
                        openRecommendationModal();
                      }, 300);
                    }
                  }}
                >
                  <Calculator className="h-5 w-5 mr-3 text-teal-400" />
                  <span>Recommend</span>
                </button>
                <button
                  className="flex items-center w-full px-4 py-3 text-white font-medium rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors text-left"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    // Open the comparison modal if available, otherwise navigate to the compare page
                    if (openComparisonModal && typeof openComparisonModal === 'function') {
                      setTimeout(() => {
                        openComparisonModal();
                      }, 300);
                    }
                  }}
                >
                  <Scale className="h-5 w-5 mr-3 text-teal-400" />
                  <span>Compare</span>
                </button>
                <Link
                  to="/mesh-advisor"
                  className="flex items-center px-4 py-3 text-white font-medium rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Wifi className="h-5 w-5 mr-3 text-teal-400" />
                  <span>Home Advisor</span>
                </Link>
              </div>
            </div>
            
            {/* WhatsApp Contact */}
            <div className="px-4 py-3 bg-gray-800/50 rounded-lg">
              <div className="text-sm text-gray-400 mb-2">Need help?</div>
              <a
                href="https://wa.me/27600672028"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 text-white"
              >
                <div className="p-2 rounded-full bg-green-600/20">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </div>
                <div>
                  <div className="font-bold">WhatsApp Us</div>
                  <div className="text-xs text-gray-400">Quick Support</div>
                </div>
              </a>
            </div>
            
            {/* Client Portal Link */}
            <div className="px-4 py-3 bg-gray-800/50 rounded-lg">
              <div className="text-sm text-gray-400 mb-2">Account Access</div>
              <a
                href="https://portal.wondernet.co.za"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 text-white"
              >
                <div className="p-2 rounded-full bg-blue-600/20">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    <path d="M12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <div className="font-bold">Client Portal</div>
                  <div className="text-xs text-gray-400">Manage Account</div>
                </div>
              </a>
            </div>
            
            {/* Close Button */}
            <button
              className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg flex items-center justify-center space-x-2"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <X className="h-5 w-5" />
              <span>Close Menu</span>
            </button>
          </div>
        </div>
      )}
      
      {/* Bottom Navigation */}
      <MobileBottomNav onMenuClick={handleMobileMenuAction} />
    </motion.div>
  );
};

export default MobileNavComponent;